import firebase from '../config/firebase.config';
import httpClient from '../config/httpClient';

const POLL_INTERVAL_MS = 15000;

async function authedPost(path, body) {
  const user = firebase.auth().currentUser;
  if (!user) throw new Error('User not authenticated');
  const idToken = await user.getIdToken();
  const { data } = await httpClient.post(path, body, {
    headers: { Authorization: `Bearer ${idToken}` },
  });
  return data;
}

// companyDatabase/{companyId}/integrations is Cloud-Functions-only in
// firestore.rules, so this polls getIntegrationImportLogs instead of the
// onSnapshot listener it used to be. Signature (returns an unsubscribe fn)
// is unchanged so callers don't need to change.
export const subscribeToIntegrationImportLogs = (
  companyId,
  integrationDocId,
  onUpdate
) => {
  let cancelled = false;

  const poll = async () => {
    try {
      const { logs } = await authedPost('/getIntegrationImportLogs', {
        companyId,
        integrationDocId,
      });
      if (!cancelled) {
        onUpdate(
          (logs || []).map((log) => ({
            ...log,
            importedAt: log.importedAt ? new Date(log.importedAt) : null,
          }))
        );
      }
    } catch (err) {
      console.error(`[${integrationDocId}] import logs poll error:`, err);
      if (!cancelled) onUpdate([]);
    }
  };

  poll();
  const intervalId = setInterval(poll, POLL_INTERVAL_MS);

  return () => {
    cancelled = true;
    clearInterval(intervalId);
  };
};

export const getIntegrationImportLogDetails = async (
  companyId,
  integrationDocId,
  logId
) => {
  try {
    const { data } = await authedPost('/getIntegrationImportLogDetail', {
      companyId,
      integrationDocId,
      logId,
    });
    return data || { employees: [] };
  } catch (err) {
    console.error(`[${integrationDocId}] import log detail error:`, err);
    return { employees: [] };
  }
};

// Same polling approach for connection status — replaces each provider's
// subscribeTo*Status onSnapshot listener. Returns { exists, data } so each
// provider file keeps doing its own field-shaping/defaults, unchanged.
export const subscribeToIntegrationStatus = (
  companyId,
  integrationDocId,
  onUpdate
) => {
  let cancelled = false;

  const poll = async () => {
    try {
      const { exists, data } = await authedPost('/getIntegrationStatus', {
        companyId,
        integrationDocId,
      });
      if (!cancelled) onUpdate({ exists, data: data || {} });
    } catch (err) {
      console.error(`[${integrationDocId}] status poll error:`, err);
      if (!cancelled) onUpdate({ exists: false, data: {} });
    }
  };

  poll();
  const intervalId = setInterval(poll, POLL_INTERVAL_MS);

  return () => {
    cancelled = true;
    clearInterval(intervalId);
  };
};
